import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { CircularProgress } from '@mui/material';
import ReactECharts from 'echarts-for-react';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { clinicalAnalyticsActions } from 'store/clinical';
import { displayModeEnums, goalStatusColors, targetStatusColors, getGoalMasteryLine } from './constants';
import { InsightsDonutChart } from './components/apexDonutChart';
import { ChartInfoPopover } from './components/chartInfoPopover';
import { TargetProgressChart } from './components/targetProgressChart';
import { buildBucketTooltipHtml, buildEventBuckets, EVENT_TOOLTIP_FONT_FAMILY } from './components/chartEventHelpers';
import {
    buildTargetLegendOption,
    formatChartValueByDataType,
    getChartValueLabelByDataType,
    hasChartValue,
    isPercentageDataType,
    TARGET_LEGEND_GRID_BOTTOM,
} from './components/dataTypeChart.helpers';
import moment from 'moment';
import { graphValueModeEnums, Images } from 'utils';
import { GraphValueModeToggle, toValueModeParam } from './components/graphValueModeToggle';

const FONT_FAMILY = '"GT Walsheim Pro", "Open Sans", sans-serif';

const parseInsightDate = (value) => moment(value, ['YYYY-MM-DD', 'YYYY-MM', 'GGGG-[W]WW'], true);

const formatInsightDate = (value, monthFormat = 'MMM', dayFormat = 'DD MMM') => {
    const parsed = parseInsightDate(value);
    if (!parsed.isValid()) return value;
    if (value?.includes('-W')) return parsed.startOf('isoWeek').format(dayFormat);
    return parsed.format(value?.length > 7 ? dayFormat : monthFormat);
};

const getStatusClassName = (status) => (status ? `status-${String(status).toLowerCase()}` : 'status-default');

const shouldIncludeEvent = (event, showClientEvents, showGoalEvents) => {
    if (event.type === 'Client' && !showClientEvents) return false;
    if ((event.type === 'Goal' || event.type === 'Target') && !showGoalEvents) return false;
    return true;
};

const filterEvents = (events = [], showClientEvents, showGoalEvents) =>
    events.filter((event) => shouldIncludeEvent(event, showClientEvents, showGoalEvents));

const getStatusDistributionMeta = (targetStatusDistribution = []) => ({
    series: targetStatusDistribution.map((item) => item.value),
    labels: targetStatusDistribution.map((item) => item.label),
    colors: targetStatusDistribution.map((item) => targetStatusColors[item.label] || '#D0D5DD'),
    totalCount: targetStatusDistribution.reduce((sum, item) => sum + (item?.value || 0), 0),
});

const buildCumulativeProgressOption = ({ data, displayMode, showMastery, goalMastery, totalCount }) => {
    if (!data?.length) return null;

    const categories = data.map((point) => point.date);
    const isPercent = displayMode === displayModeEnums.PERCENT;
    const seriesData = data.map((point) => (isPercent ? point.cumulative : point.cumulativeCount));
    const masteryLine = getGoalMasteryLine(goalMastery, isPercent, data.at(-1)?.totalCount ?? totalCount);
    const yAxisMax = isPercent ? 100 : Math.max(totalCount || 0, ...seriesData, 0);
    const markLineData =
        masteryLine && showMastery
            ? [
                  {
                      yAxis: masteryLine.value,
                      label: {
                          formatter: masteryLine.label,
                          position: 'insideEndTop',
                          fontSize: 10,
                          color: '#00C48C',
                          fontFamily: FONT_FAMILY,
                      },
                      lineStyle: { color: '#00C48C', type: 'dashed', width: 1.5 },
                  },
              ]
            : [];

    return {
        tooltip: {
            trigger: 'axis',
            textStyle: { fontFamily: FONT_FAMILY, fontSize: 12 },
            formatter: (params) => {
                if (!params?.length) return '';
                const axisValue = params[0]?.axisValue;
                const value = params[0]?.value;
                const label = formatInsightDate(axisValue, 'MMM YYYY', 'DD MMM YYYY');
                return `<b style="color:#0C66E4">${label}</b><br/>Mastery: <b>${value}${isPercent ? '%' : ''}</b>`;
            },
        },
        grid: { left: '3%', right: '4%', top: '5%', bottom: '8%', containLabel: true },
        xAxis: {
            type: 'category',
            boundaryGap: false,
            data: categories,
            axisLine: { lineStyle: { color: '#E5E7EB' } },
            axisTick: { show: false },
            axisLabel: {
                color: '#98A2B3',
                fontSize: 11,
                fontFamily: FONT_FAMILY,
                formatter: (value) => formatInsightDate(value),
            },
            splitNumber: data.length > 14 ? 7 : undefined,
        },
        yAxis: {
            type: 'value',
            min: 0,
            max: yAxisMax,
            minInterval: isPercent ? undefined : 1,
            axisLine: { show: false },
            axisTick: { show: false },
            axisLabel: {
                color: '#98A2B3',
                fontSize: 11,
                fontFamily: FONT_FAMILY,
                formatter: (value) => (isPercent ? `${value}%` : `${Math.round(value)}`),
            },
            splitLine: { lineStyle: { color: '#F2F4F7' } },
        },
        series: [
            {
                type: 'line',
                data: seriesData,
                smooth: false,
                symbol: 'circle',
                symbolSize: 6,
                lineStyle: { color: '#0C66E4', width: 2.5 },
                itemStyle: { color: '#0C66E4', borderColor: '#fff', borderWidth: 2 },
                areaStyle: {
                    color: {
                        type: 'linear',
                        x: 0,
                        y: 0,
                        x2: 0,
                        y2: 1,
                        colorStops: [
                            { offset: 0, color: 'rgba(12,102,228,0.10)' },
                            { offset: 1, color: 'rgba(12,102,228,0.01)' },
                        ],
                    },
                },
                markLine:
                    markLineData.length > 0
                        ? {
                              symbol: 'none',
                              data: markLineData,
                          }
                        : undefined,
            },
        ],
    };
};

const buildAggregateTargetProgressOption = ({ targets, events = [] }) => {
    if (!targets?.length) return null;

    const categories = Array.from(new Set(targets.flatMap((target) => (target?.progressData || []).map((point) => point.date))));

    if (!categories.length) return null;

    const palette = ['#0C66E4', '#12B76A', '#F79009', '#7A5AF8', '#EE46BC', '#17B26A', '#F04438', '#2E90FA'];
    const dataType = targets.find((target) => target?.dataType)?.dataType;
    const valueLabel = getChartValueLabelByDataType(dataType);
    const isPercentType = isPercentageDataType(dataType);
    const series = targets.map((target, index) => {
        const pointMap = new Map((target?.progressData || []).map((point) => [point.date, point.value]));

        return {
            name: target.targetName,
            type: 'line',
            data: categories.map((category) => pointMap.get(category) ?? null),
            connectNulls: true,
            smooth: false,
            symbol: 'circle',
            showSymbol: true,
            symbolSize: (value) => (value === null || value === undefined ? 0 : 5),
            lineStyle: { color: palette[index % palette.length], width: 2 },
            itemStyle: { color: palette[index % palette.length], borderColor: '#fff', borderWidth: 2 },
        };
    });
    const numericValues = series.flatMap((item) => item.data).filter((value) => value !== null && value !== undefined);
    const yAxisMax = Math.max(...numericValues, 0);
    const yAxisDisplayMax = isPercentType ? 100 : Math.max(Math.ceil(yAxisMax * 1.12), yAxisMax + 1, 1);
    const filteredEvents = events.filter((event) => event.type === 'Client' || event.type === 'Goal');
    const eventBuckets = buildEventBuckets(filteredEvents, categories);
    const eventBucketMap = new Map(eventBuckets.map((bucket) => [bucket.key, bucket]));
    const eventMarkerY = Math.max(yAxisMax, yAxisDisplayMax * 0.9);
    const markLineData = eventBuckets.map((bucket) => ({
        xAxis: bucket.key,
        lineStyle: {
            color: bucket.color,
            type: 'dashed',
            width: 1.5,
        },
        label: { show: false },
    }));
    const eventMarkerSeries = eventBuckets.some((bucket) => bucket.count > 1)
        ? [
              {
                  name: 'Event Counts',
                  type: 'scatter',
                  silent: true,
                  data: eventBuckets
                      .filter((bucket) => bucket.count > 1)
                      .map((bucket) => ({
                          value: [bucket.key, eventMarkerY],
                          count: bucket.count,
                          itemStyle: { color: bucket.color },
                          label: {
                              show: true,
                              formatter: `${bucket.count}`,
                              color: '#FFFFFF',
                              fontSize: 10,
                              fontWeight: 700,
                              fontFamily: FONT_FAMILY,
                          },
                      })),
                  symbol: 'circle',
                  symbolSize: 18,
                  z: 6,
              },
          ]
        : [];
    const eventHoverSeries =
        eventBuckets.length > 0
            ? [
                  {
                      name: 'Event Hover',
                      type: 'bar',
                      data: categories.map((category) => (eventBucketMap.has(category) ? yAxisDisplayMax : 0)),
                      barWidth: 12,
                      barGap: '-100%',
                      itemStyle: { color: 'rgba(0, 0, 0, 0)' },
                      emphasis: { itemStyle: { color: 'rgba(0, 0, 0, 0)' } },
                      tooltip: { show: false },
                      z: 1,
                  },
              ]
            : [];

    return {
        tooltip: {
            trigger: 'axis',
            axisPointer: { type: 'line' },
            extraCssText: `box-shadow:none;background:transparent;border:none;padding:0;font-family:${EVENT_TOOLTIP_FONT_FAMILY};`,
            textStyle: { fontFamily: FONT_FAMILY, fontSize: 12 },
            formatter: (params) => {
                if (!params?.length) return '';
                const axisValue = params[0].axisValue;
                const label = formatInsightDate(axisValue, 'MMM YYYY', 'DD MMM YYYY');
                const lineRows = params
                    .filter((param) => param.seriesType === 'line' && hasChartValue(param.value))
                    .map(
                        (param) => `
                        <div style="display:flex; align-items:center; gap:6px;">
                            <span style="display:inline-block;width:10px;height:10px;border-radius:50%;background:${param.color};"></span>
                            <p style="color:#344054; font-size:12px; font-weight:400; line-height:18px; margin:0;">
                                ${param.seriesName}:
                                <span style="color:#344054; font-size:12px; font-weight:600; line-height:18px;"> ${formatChartValueByDataType(param.value, dataType)}</span>
                            </p>
                        </div>
                    `
                    )
                    .join('') ||
                    // Only targets with data that day are listed; on a day none has any, one line says so.
                    '<p style="color:#667085; font-size:12px; line-height:18px; margin:0;">No session data</p>';
                const eventTooltip = buildBucketTooltipHtml(eventBucketMap.get(axisValue));

                return `
                    <div style="padding:12px 8px; background:white; border-radius:8px; box-shadow:0 0 6px 0 rgba(138, 138, 138, 0.24); margin-bottom:${eventTooltip ? '8px' : '0'}; font-family:${EVENT_TOOLTIP_FONT_FAMILY};">
                        <p style="color:#0C66E4; font-size:12px; font-weight:600; line-height:18px; margin:0 0 6px;">${label}</p>
                        <div style="display:flex; flex-direction:column; gap:4px;">${lineRows}</div>
                    </div>
                    ${eventTooltip}
                `;
            },
        },
        legend: buildTargetLegendOption({ names: series.map((item) => item.name), fontFamily: FONT_FAMILY }),
        grid: { left: '7%', right: '4%', top: '8%', bottom: TARGET_LEGEND_GRID_BOTTOM, containLabel: true },
        xAxis: {
            type: 'category',
            boundaryGap: false,
            data: categories,
            name: 'Time',
            nameLocation: 'middle',
            nameGap: 30,
            nameTextStyle: {
                color: '#667085',
                fontSize: 12,
                fontWeight: 600,
                fontFamily: FONT_FAMILY,
            },
            axisLine: { lineStyle: { color: '#E5E7EB' } },
            axisTick: { show: false },
            axisLabel: {
                color: '#98A2B3',
                fontSize: 11,
                fontFamily: FONT_FAMILY,
                formatter: (value) => formatInsightDate(value),
            },
        },
        yAxis: {
            type: 'value',
            min: 0,
            max: yAxisDisplayMax,
            name: valueLabel,
            nameLocation: 'middle',
            nameGap: 52,
            nameRotate: 90,
            nameTextStyle: {
                color: '#667085',
                fontSize: 12,
                fontWeight: 600,
                fontFamily: FONT_FAMILY,
            },
            axisLine: { show: false },
            axisTick: { show: false },
            axisLabel: {
                color: '#98A2B3',
                fontSize: 11,
                fontFamily: FONT_FAMILY,
                formatter: (value) => formatChartValueByDataType(value, dataType),
            },
            splitLine: { lineStyle: { color: '#F2F4F7' } },
        },
        series: [
            ...series.map((item) => ({
                ...item,
                markLine: markLineData.length > 0 ? { symbol: 'none', data: markLineData } : undefined,
            })),
            ...eventHoverSeries,
            ...eventMarkerSeries,
        ],
    };
};

const StatusBadge = ({ status }) => (
    <span className={`status-badge ${getStatusClassName(status)}`}>
        <span className={`status-badge-dot ${getStatusClassName(status)}`} />
        {status}
    </span>
);

const TargetStatusDistributionSection = ({ targetStatusDistribution, statusSeries, statusLabels, statusDonutColors }) => {
    if (!statusSeries.length) return null;

    return (
        <div className="insights-chart-card">
            <p className="insights-chart-card-title">Target Status Distribution</p>
            <div className="insights-status-distribution-layout">
                <div className="insights-status-distribution-fixed">
                    <InsightsDonutChart series={statusSeries} labels={statusLabels} colors={statusDonutColors} height={180} />
                </div>
                <div className="donut-legend insights-status-distribution-flex">
                    {targetStatusDistribution?.map((item, idx) => (
                        <div key={item.label} className="donut-legend-item">
                            <div className="donut-legend-left">
                                <span className={`donut-legend-dot ${getStatusClassName(item.label)}`} />
                                <span>{item.label}</span>
                            </div>
                            <span className="donut-legend-value">{item.value}</span>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

const TargetProgressSection = ({ targets }) => {
    if (!targets.length) return null;
    const sortedTargets = [...targets].sort((left, right) => {
        const leftHasData =
            Array.isArray(left?.progressData) && left.progressData.some((point) => point?.value !== null && point?.value !== undefined);
        const rightHasData =
            Array.isArray(right?.progressData) && right.progressData.some((point) => point?.value !== null && point?.value !== undefined);

        if (leftHasData === rightHasData) {
            return (left?.targetName || '').localeCompare(right?.targetName || '');
        }

        return leftHasData ? -1 : 1;
    });

    return (
        <div>
            <p className="insights-section-label insights-section-label-top">Target Progress</p>
            {sortedTargets.map((target) => (
                <div key={target.targetId} className="target-progress-card">
                    <div className="target-progress-card-header">
                        <div className="insights-chart-title-row">
                            <p className="target-progress-card-name">{target.targetName}</p>
                            <ChartInfoPopover
                                compact={true}
                                title="Target Chart Guide"
                                items={[
                                    {
                                        label: 'Progress',
                                        text: 'Shows target session performance over time.',
                                        style: { background: '#0C66E4' },
                                    },
                                    {
                                        label: 'Trend',
                                        text: 'Shows the overall direction of target performance when enough data exists.',
                                        style: { background: '#FF6B6B' },
                                    },
                                    {
                                        label: 'Mastery',
                                        text: 'Dashed threshold line showing the target mastery benchmark.',
                                        type: 'dash',
                                        style: { borderTopColor: '#12B76A' },
                                    },
                                    {
                                        label: 'Client Events',
                                        text: 'Vertical markers for client-level events within the selected range.',
                                        type: 'vertical',
                                        style: { borderColor: '#0C66E4' },
                                    },
                                    {
                                        label: 'Goal Events',
                                        text: 'Vertical markers for goal and target events within the selected range.',
                                        type: 'vertical',
                                        style: { borderColor: '#2A8E6D' },
                                    },
                                ]}
                            />
                        </div>
                        <StatusBadge status={target.status} colorMap={targetStatusColors} />
                    </div>
                    <TargetProgressChart
                        data={target.progressData}
                        dataType={target.dataType}
                        mastery={target.mastery}
                        events={target.events}
                    />
                </div>
            ))}
        </div>
    );
};

export const GoalDrilldown = ({
    clientId,
    goalId,
    dateRange,
    displayMode,
    showMastery = true,
    showClientEvents = true,
    showGoalEvents = true,
    onBack,
}) => {
    const dispatch = useDispatch();
    const drilldown = useSelector((state) => state.clinicalAnalytics.goalDrilldown);
    const aggregateTargetChartRef = useRef(null);
    const [valueMode, setValueMode] = useState(graphValueModeEnums.DEFAULT);
    // Kept across refetches (the drilldown is reset on every fetch), so the toggle does not flicker away.
    const [goalDataType, setGoalDataType] = useState();
    const valueModeParam = toValueModeParam(goalDataType, valueMode);

    useEffect(() => {
        const dataType = drilldown?.targetProgress?.find((target) => target?.dataType)?.dataType;
        if (dataType) setGoalDataType(dataType);
    }, [drilldown?.targetProgress]);

    useEffect(() => {
        if (clientId && goalId) {
            dispatch(
                clinicalAnalyticsActions.getGoalDrilldown(clientId, goalId, {
                    startDate: dateRange.start,
                    endDate: dateRange.end,
                    valueMode: valueModeParam,
                })
            );
        }

        return () => {
            dispatch(clinicalAnalyticsActions.resetGoalDrilldown());
        };
    }, [clientId, dateRange.end, dateRange.start, dispatch, goalId, valueModeParam]);

    const { goalName, domain, status, cumulativeProgress, goalMastery, targetStatusDistribution, targetProgress } = drilldown || {};
    const {
        series: statusSeries,
        labels: statusLabels,
        colors: statusDonutColors,
        totalCount,
    } = useMemo(() => getStatusDistributionMeta(targetStatusDistribution || []), [targetStatusDistribution]);
    const filteredTargetProgress = useMemo(
        () =>
            (targetProgress || []).map((target) => ({
                ...target,
                events: filterEvents(target.events || [], showClientEvents, showGoalEvents),
            })),
        [showClientEvents, showGoalEvents, targetProgress]
    );
    const aggregateTargetProgressOption = useMemo(
        () =>
            buildAggregateTargetProgressOption({
                targets: filteredTargetProgress,
                events: filterEvents(cumulativeProgress?.events || [], showClientEvents, showGoalEvents).filter(
                    (event) => event.type === 'Client' || event.type === 'Goal'
                ),
            }),
        [cumulativeProgress?.events, filteredTargetProgress, showClientEvents, showGoalEvents]
    );
    const handleDownloadAggregateTargetChart = () => {
        const chartInstance = aggregateTargetChartRef?.current?.getEchartsInstance?.();
        if (!chartInstance) return;

        const imageUrl = chartInstance.getDataURL({
            type: 'png',
            pixelRatio: 2,
            backgroundColor: '#FFFFFF',
            excludeComponents: ['toolbox'],
        });
        const link = document.createElement('a');
        link.href = imageUrl;
        link.download = `all-targets-progress-${goalId || 'goal'}-${moment().format('YYYYMMDD-HHmmss')}.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    if (!drilldown) {
        return (
            <div className="insights-loading">
                <CircularProgress size={32} />
            </div>
        );
    }

    return (
        <div>
            <button className="insights-back-btn" onClick={onBack}>
                <ArrowBackIcon fontSize="small" /> Back to Goals
            </button>

            <div className="goal-drilldown-header">
                <div>
                    <h3 className="goal-drilldown-title">{goalName}</h3>
                    <div className="goal-drilldown-meta">
                        <span>Goal Insights</span>
                        <span className="goal-drilldown-meta-separator">&#8226;</span>
                        <span>{domain}</span>
                        <span className="goal-drilldown-meta-separator">&#8226;</span>
                        <StatusBadge status={status} colorMap={goalStatusColors} />
                    </div>
                </div>
                <GraphValueModeToggle dataType={goalDataType} value={valueMode} onChange={setValueMode} />
            </div>

            <div className="insights-chart-card">
                <div className="insights-chart-card-header">
                    <div>
                        <div className="insights-chart-title-row">
                            <p className="insights-chart-card-title">Cumulative Goal Progress</p>
                            <ChartInfoPopover
                                title="Goal Progress Guide"
                                items={[
                                    {
                                        label: 'Mastery',
                                        text: 'Shows cumulative mastered target progress over time.',
                                        style: { background: '#0C66E4' },
                                    },
                                    {
                                        label: 'Mastery',
                                        text: 'Dashed threshold line showing the mastery benchmark.',
                                        type: 'dash',
                                        style: { borderTopColor: '#00C48C' },
                                    },
                                ]}
                            />
                        </div>
                        <p className="insights-chart-card-subtitle insights-chart-card-subtitle-system">
                            {displayMode === displayModeEnums.COUNT
                                ? 'Number of mastered targets over time'
                                : 'Percentage of targets mastered over time'}
                        </p>
                    </div>
                </div>
                <CumulativeProgressChart
                    data={cumulativeProgress?.data || []}
                    showMastery={showMastery}
                    displayMode={displayMode}
                    goalMastery={goalMastery}
                    totalCount={totalCount}
                />
            </div>

            <TargetStatusDistributionSection
                targetStatusDistribution={targetStatusDistribution}
                statusSeries={statusSeries}
                statusLabels={statusLabels}
                statusDonutColors={statusDonutColors}
            />

            {aggregateTargetProgressOption && (
                <div className="insights-chart-card">
                    <div className="insights-chart-card-header">
                        <div>
                            <div className="insights-chart-title-row">
                                <p className="insights-chart-card-title">All Targets Progress</p>
                                <ChartInfoPopover
                                    title="All Targets Chart Guide"
                                    items={[
                                        {
                                            label: 'Target Lines',
                                            text: 'Shows session performance for all targets together in one chart.',
                                            style: { background: '#0C66E4' },
                                        },
                                        {
                                            label: 'Client Events',
                                            text: 'Vertical markers for client-level events within the selected range.',
                                            type: 'vertical',
                                            style: { borderColor: '#0C66E4' },
                                        },
                                        {
                                            label: 'Goal Events',
                                            text: 'Vertical markers for goal-level events within the selected range.',
                                            type: 'vertical',
                                            style: { borderColor: '#2A8E6D' },
                                        },
                                    ]}
                                />
                            </div>
                            <p className="insights-chart-card-subtitle insights-chart-card-subtitle-system">
                                Combined target performance across the selected date range
                            </p>
                        </div>
                        <button className="insights-export-btn insights-export-btn-inline" onClick={handleDownloadAggregateTargetChart}>
                            <img src={Images.downloadCloudBlue} alt="download" className="insights-export-icon" />
                        </button>
                    </div>
                    <ReactECharts
                        ref={aggregateTargetChartRef}
                        option={aggregateTargetProgressOption}
                        notMerge={true}
                        className="insights-chart-medium"
                        opts={{ renderer: 'canvas' }}
                    />
                </div>
            )}

            <TargetProgressSection targets={filteredTargetProgress} />
        </div>
    );
};

/** Cumulative Progress — ECharts line chart */
const CumulativeProgressChart = ({ data, showMastery, displayMode, goalMastery, totalCount }) => {
    const option = useMemo(
        () => buildCumulativeProgressOption({ data, displayMode, showMastery, goalMastery, totalCount }),
        [data, displayMode, showMastery, goalMastery, totalCount]
    );

    if (!option) {
        return (
            <div className="insights-empty">
                <p>No progress data</p>
            </div>
        );
    }

    return <ReactECharts option={option} notMerge={true} style={{ height: 250, width: '100%' }} opts={{ renderer: 'svg' }} />;
};
